import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileQuestion, Download, XCircle, ExternalLink, FileText } from "lucide-react";

const TONE = {
  requested: "border-amber-200 bg-amber-50 text-amber-700",
  fulfilled: "border-emerald-200 bg-emerald-50 text-emerald-700",
  cancelled: "border-slate-200 bg-slate-50 text-slate-500",
};
const RESP_LABEL = { server: "Server action", admin: "Admin review", client: "Client action" };
const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");

export default function AdminDocumentRequests() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [reqs, setReqs] = useState([]);
  const [detail, setDetail] = useState(null);
  const status = params.get("status") || "all";
  const responsible = params.get("responsible") || "all";
  const activity = params.get("activity") || "all";

  const setParam = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v === "all" || !v) next.delete(k); else next.set(k, v); });
    setParams(next);
  };

  const load = () => {
    const p = {};
    if (status !== "all") p.status = status;
    if (responsible !== "all") p.responsible = responsible;
    if (activity !== "all") p.activity = activity;
    api.get("/admin/document-requests", { params: p }).then(({ data }) => setReqs(data)).catch(() => {});
  };
  useEffect(load, [status, responsible, activity]);

  const openDetail = (id) => api.get(`/admin/document-requests/${id}`).then(({ data }) => setDetail(data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));

  const cancel = async (id) => {
    try { await api.post(`/document-requests/${id}/cancel`); toast.success("Cancelled"); load(); if (detail?.id === id) openDetail(id); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-slate-900"><FileQuestion className="h-6 w-6 text-slate-400" /> Document Requests</h2>
        <p className="text-slate-500">Click any request to see full details, history, and files. Track by status and responsibility.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={status} onValueChange={(v) => setParam({ status: v })}>
          <SelectTrigger data-testid="request-status-filter" className="w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="requested">Requested (open)</SelectItem>
            <SelectItem value="fulfilled">Fulfilled</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={responsible} onValueChange={(v) => setParam({ responsible: v })}>
          <SelectTrigger data-testid="request-responsible-filter" className="w-56"><SelectValue placeholder="Any responsibility" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any responsibility</SelectItem>
            <SelectItem value="server">Server action required</SelectItem>
            <SelectItem value="admin">Admin review required</SelectItem>
            <SelectItem value="client">Client action required</SelectItem>
          </SelectContent>
        </Select>
        <Select value={activity} onValueChange={(v) => setParam({ activity: v })}>
          <SelectTrigger data-testid="request-activity-filter" className="w-60"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any activity</SelectItem>
            <SelectItem value="active">Active — requires action</SelectItem>
            <SelectItem value="awaiting_payment">Awaiting payment (not active)</SelectItem>
            <SelectItem value="fulfilled">Fulfilled</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <span data-testid="requests-total-count" className="text-sm text-slate-500"><span className="font-semibold text-slate-900">{reqs.length}</span> request(s)</span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3">Job #</th><th className="px-5 py-3">Client</th>
              <th className="px-5 py-3">Description</th><th className="px-5 py-3">Responsible</th>
              <th className="px-5 py-3">Status</th><th className="px-5 py-3">Created</th><th className="px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {reqs.map((r) => (
              <tr key={r.id} data-testid={`admin-request-row-${r.id}`} onClick={() => openDetail(r.id)} className="cursor-pointer hover:bg-slate-50">
                <td className="px-5 py-3 font-mono-arps font-semibold text-indigo-700">#{r.job_number}</td>
                <td className="px-5 py-3 text-slate-500">{r.client_name}</td>
                <td className="px-5 py-3 text-slate-700">{r.description}</td>
                <td className="px-5 py-3">
                  {r.activity === "awaiting_payment"
                    ? <span data-testid={`request-awaiting-badge-${r.id}`} className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">Awaiting payment</span>
                    : r.responsible_party
                      ? <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-xs font-semibold text-indigo-700">{RESP_LABEL[r.responsible_party]}</span>
                      : <span className="text-xs text-slate-400">—</span>}
                </td>
                <td className="px-5 py-3"><span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONE[r.status]}`}>{r.status}</span></td>
                <td className="px-5 py-3 text-slate-400">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" data-testid={`admin-view-request-${r.id}`} onClick={() => openDetail(r.id)}>View</Button>
                    {r.status === "fulfilled" && r.fulfilled_document_id && (
                      <a href={`${API}/files/${r.fulfilled_document_id}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><Download className="h-3.5 w-3.5" /></Button></a>
                    )}
                    {r.status === "requested" && (
                      <Button data-testid={`admin-cancel-request-${r.id}`} size="sm" variant="outline" onClick={() => cancel(r.id)}><XCircle className="mr-1 h-3.5 w-3.5" /> Cancel</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {reqs.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400">No requests.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="request-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>Document Request — #{detail.job_number}</DialogTitle>
                <DialogDescription>
                  <span className={`mr-2 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONE[detail.status]}`}>{detail.status}</span>
                  {detail.responsible_party ? `Responsible: ${RESP_LABEL[detail.responsible_party]}` : "No open action (closed)"}
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm" data-testid="request-detail-fields">
                <div className="col-span-2"><span className="text-slate-500">Document requested</span><p className="font-medium text-slate-900">{detail.description}</p></div>
                {detail.category && <div><span className="text-slate-500">Category</span><p className="text-slate-800">{detail.category}</p></div>}
                {detail.note && <div className="col-span-2"><span className="text-slate-500">Note</span><p className="text-slate-800">{detail.note}</p></div>}
                <div><span className="text-slate-500">Created by</span><p className="text-slate-800">{detail.created_by || "—"}</p></div>
                <div><span className="text-slate-500">Created</span><p className="text-slate-800">{fmt(detail.created_at)}</p></div>
                <div><span className="text-slate-500">Client</span><p className="text-slate-800">{detail.client_name}</p></div>
                <div><span className="text-slate-500">Related job</span>
                  <button data-testid="request-detail-job-link" onClick={() => nav(`/admin/jobs/${detail.order_id}`)} className="inline-flex items-center gap-1 font-semibold text-indigo-600 hover:underline">#{detail.job_number} <ExternalLink className="h-3 w-3" /></button>
                  <span className="ml-1 text-xs text-slate-400">({detail.order_status})</span>
                </div>
                <div><span className="text-slate-500">Assigned server</span><p className="text-slate-800">{detail.assigned_server_name || "Unassigned"}</p></div>
                {detail.fulfilled_at && <div><span className="text-slate-500">Fulfilled</span><p className="text-slate-800">{fmt(detail.fulfilled_at)} · {detail.fulfilled_by}</p></div>}
                {detail.cancelled_at && <div><span className="text-slate-500">Cancelled</span><p className="text-slate-800">{fmt(detail.cancelled_at)}</p></div>}
              </div>

              <div className="mt-2">
                <p className="mb-1 text-xs font-semibold uppercase text-slate-500">Related files / documents</p>
                <div className="space-y-1" data-testid="request-detail-files">
                  {(detail.related_files || []).length === 0 && <p className="text-sm text-slate-400">No files uploaded to this job yet.</p>}
                  {(detail.related_files || []).map((f) => (
                    <a key={f.id} href={`${API}/files/${f.id}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                      <FileText className="h-4 w-4 text-slate-400" />
                      <span className="flex-1 truncate">{f.name || f.id}</span>
                      {f.is_fulfillment && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-700">Fulfillment</span>}
                      <span className="text-xs text-slate-400">{f.category}</span>
                    </a>
                  ))}
                </div>
              </div>

              <div className="mt-2">
                <p className="mb-1 text-xs font-semibold uppercase text-slate-500">Request history / audit trail</p>
                <ol className="space-y-1.5" data-testid="request-detail-history">
                  {(detail.history || []).map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-indigo-400" />
                      <div><span className="font-medium text-slate-800">{h.event.replace(/_/g, " ")}</span>
                        <span className="ml-2 text-xs text-slate-400">{fmt(h.at)}{h.by ? ` · ${h.by}` : ""}</span>
                        {h.detail && <p className="text-xs text-slate-500">{h.detail}</p>}</div>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="mt-3 flex flex-wrap gap-2 border-t pt-3">
                <Button variant="outline" data-testid="request-detail-view-job" onClick={() => nav(`/admin/jobs/${detail.order_id}`)}><ExternalLink className="mr-1.5 h-4 w-4" /> View job</Button>
                {detail.status === "fulfilled" && detail.fulfilled_document_id && (
                  <a href={`${API}/files/${detail.fulfilled_document_id}`} target="_blank" rel="noreferrer"><Button variant="outline"><Download className="mr-1.5 h-4 w-4" /> View fulfilled document</Button></a>
                )}
                {detail.status === "requested" && (
                  <Button data-testid="request-detail-cancel" variant="outline" onClick={() => cancel(detail.id)} className="text-red-600"><XCircle className="mr-1.5 h-4 w-4" /> Cancel request</Button>
                )}
                {detail.status === "requested" && detail.awaiting_payment && (
                  <span data-testid="request-awaiting-payment-hint" className="self-center text-xs font-medium text-amber-600">Awaiting client payment — server assignment is not yet available.</span>
                )}
                {detail.status === "requested" && !detail.awaiting_payment && detail.responsible_party === "admin" && detail.assignment_ready && (
                  <span data-testid="request-assign-hint" className="self-center text-xs text-amber-600">Assign a server on the job to enable fulfillment.</span>
                )}
                {detail.status === "requested" && detail.responsible_party === "server" && (
                  <span className="self-center text-xs text-slate-500">Awaiting fulfillment by {detail.assigned_server_name || "the assigned server"}.</span>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
